import { createContext, useContext, useState, useEffect } from "react";

const UserContext = createContext(null);

// Generate or get user ID
const getUserId = () => {
  let userId = localStorage.getItem("upnext_user_id");
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("upnext_user_id", userId);
  }
  return userId;
};

export function UserProvider({ children }) {
  const [userId] = useState(getUserId);
  const [bookmarks, setBookmarks] = useState([]);

  // Load bookmarks on mount
  useEffect(() => {
    const saved = localStorage.getItem("upnext_bookmarks");
    if (saved) {
      try {
        setBookmarks(JSON.parse(saved));
      } catch {
        setBookmarks([]);
      }
    }
  }, []);

  // Save bookmarks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("upnext_bookmarks", JSON.stringify(bookmarks));
  }, [bookmarks]);

  const addBookmark = (eventId) => {
    if (!bookmarks.includes(eventId)) {
      setBookmarks(prev => [...prev, eventId]);
      return true;
    }
    return false;
  };

  const removeBookmark = (eventId) => {
    setBookmarks(prev => prev.filter(id => id !== eventId));
  };

  const toggleBookmark = (eventId) => {
    if (bookmarks.includes(eventId)) {
      removeBookmark(eventId);
      return false; // Not bookmarked anymore
    } else {
      addBookmark(eventId);
      return true; // Now bookmarked
    }
  };

  const isBookmarked = (eventId) => {
    return bookmarks.includes(eventId);
  };

  const clearAllBookmarks = () => {
    setBookmarks([]);
  };

  const value = {
    userId,
    bookmarks,
    addBookmark,
    removeBookmark,
    toggleBookmark,
    isBookmarked,
    clearAllBookmarks,
    bookmarkCount: bookmarks.length
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export default UserContext;
